import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Banknote, CreditCard, Gift, Loader2, ShieldCheck } from "lucide-react";
import { useRazorpay } from "react-razorpay";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { useShop, FREE_SHIPPING_THRESHOLD, STANDARD_SHIPPING, PRIORITY_SHIPPING } from "@/context/ShopContext";
import { useContent } from "@/context/ContentContext";
import api, { inr } from "@/lib/api";

const FIELDS = [
  { key: "name", label: "Full name", testId: "checkout-shipping-fullname", span: 2 },
  { key: "email", label: "Email", type: "email", testId: "checkout-shipping-email" },
  { key: "phone", label: "Phone", type: "tel", testId: "checkout-shipping-phone" },
  { key: "address", label: "Address (house, street)", testId: "checkout-shipping-address", span: 2 },
  { key: "landmark", label: "Landmark (optional)", required: false, span: 2 },
  { key: "city", label: "City", testId: "checkout-shipping-city" },
  { key: "state", label: "State", testId: "checkout-shipping-state" },
  { key: "pincode", label: "PIN code", testId: "checkout-shipping-pincode" },
];

const Checkout = () => {
  const { cart, subtotal, discount, coupon, clearCart, products } = useShop();
  const { c } = useContent();
  const navigate = useNavigate();
  const placed = useRef(false);
  const { Razorpay } = useRazorpay();
  const [rzpEnabled, setRzpEnabled] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", landmark: "", city: "", state: "", pincode: "" });
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [paymentMethod, setPaymentMethod] = useState("online");
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    api.get("/payments/razorpay/config").then((r) => {
      const enabled = !!r.data.enabled;
      setRzpEnabled(enabled);
      if (!enabled) setPaymentMethod("cod");
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (cart.length === 0 && !placed.current) navigate("/shop");
  }, [cart, navigate]);

  const threshold = Number(c("shipping_free_threshold", String(FREE_SHIPPING_THRESHOLD))) || FREE_SHIPPING_THRESHOLD;
  const stdFee = c("shipping_standard_fee", "") !== "" ? Number(c("shipping_standard_fee")) : STANDARD_SHIPPING;
  const priFee = c("shipping_priority_fee", "") !== "" ? Number(c("shipping_priority_fee")) : PRIORITY_SHIPPING;
  const effective = subtotal - discount;
  const shipping = shippingMethod === "priority" ? priFee : effective >= threshold ? 0 : stdFee;
  const total = effective + shipping;

  const giftEnabled = c("freebie_enabled", "false") === "true";
  const giftThreshold = Number(c("freebie_threshold", "3000")) || 3000;
  const giftProduct = products.find((p) => p.id === c("freebie_product_id", ""));
  const giftUnlocked = !!(giftEnabled && giftProduct && giftProduct.stock > 0 && effective >= giftThreshold);

  const codEnabled = c("cod_enabled", "false") === "true";

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const finishOrder = (order) => {
    placed.current = true;
    clearCart();
    navigate(`/order/${order.id}`, { state: { order } });
  };

  const openRazorpay = async (order) => {
    try {
      const rzo = await api.post("/payments/razorpay/order", { order_id: order.id });
      const options = {
        key: rzo.data.key_id,
        amount: rzo.data.amount,
        currency: "INR",
        name: "Riaas Collective",
        description: `Order #${order.id}`,
        order_id: rzo.data.razorpay_order_id,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#C86446" },
        handler: async (resp) => {
          try {
            const verified = await api.post("/payments/razorpay/verify", {
              order_id: order.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment received — order confirmed");
            finishOrder(verified.data);
          } catch {
            toast.error("Payment could not be verified — we'll confirm it manually");
            finishOrder(order);
          }
        },
        modal: {
          ondismiss: () => {
            toast("Payment skipped — your order is saved, pay via the link we send you");
            finishOrder(order);
          },
        },
      };
      const rzp = new Razorpay(options);
      rzp.on("payment.failed", () => {
        toast.error("Payment failed — your order is saved, you can pay later");
        finishOrder(order);
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment — order saved, pay later");
      finishOrder(order);
    }
  };

  const placeOrder = async () => {
    for (const f of FIELDS) {
      if (f.required === false) continue;
      if (!form[f.key]?.trim()) {
        toast.error(`Please fill in ${f.label.toLowerCase()}`);
        return;
      }
    }
    setPlacing(true);
    try {
      const method = codEnabled && paymentMethod === "cod" ? "cod" : "online";
      const r = await api.post("/orders", {
        customer: form,
        items: cart.map((i) => ({ product_id: i.id, qty: i.qty })),
        coupon_code: coupon?.code || null,
        shipping_method: shippingMethod,
        payment_method: method,
      });
      if (method === "cod") {
        toast.success("Order placed — pay when it arrives");
        finishOrder(r.data);
      } else if (rzpEnabled) {
        await openRazorpay(r.data);
      } else {
        finishOrder(r.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place the order — please try again");
    } finally {
      setPlacing(false);
    }
  };

  const codSelected = codEnabled && paymentMethod === "cod";

  return (
    <div className="mx-auto max-w-6xl px-5 pb-24 pt-32 sm:px-8 lg:pt-36" data-testid="checkout-page">
      <Link to="/shop" className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-clay/60 transition-colors hover:text-terra">
        <ArrowLeft className="h-3.5 w-3.5" /> Continue shopping
      </Link>
      <h1 className="mt-4 font-serif text-3xl tracking-tight text-espresso sm:text-4xl">Checkout</h1>

      <div className="mt-10 grid gap-10 lg:grid-cols-5">
        <Reveal className="lg:col-span-3">
          <div className="rounded-2xl border border-linen bg-white p-6 sm:p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra">Step 1 · Delivery details</p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {FIELDS.map((f) => (
                <div key={f.key} className={f.span === 2 ? "sm:col-span-2" : ""}>
                  <label className="mb-1.5 block text-xs font-semibold text-clay">{f.label}</label>
                  <input
                    data-testid={f.testId}
                    type={f.type || "text"}
                    value={form[f.key]}
                    onChange={set(f.key)}
                    className="h-11 w-full rounded-lg border border-linen bg-cream/50 px-3.5 text-sm outline-none transition-colors focus:border-terra"
                  />
                </div>
              ))}
            </div>

            <p className="mt-9 font-mono text-[10px] uppercase tracking-[0.25em] text-terra">Step 2 · Shipping method</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {[
                { id: "standard", name: "Standard insured", desc: "4–7 days · secure crushproof packaging", price: effective >= threshold ? 0 : stdFee },
                { id: "priority", name: "Priority dispatch", desc: "2–3 days · ships within 24 hours", price: priFee },
              ].map((m) => (
                <button
                  key={m.id}
                  data-testid={`shipping-method-${m.id}`}
                  onClick={() => setShippingMethod(m.id)}
                  className={`rounded-xl border p-4 text-left transition-colors duration-200 ${shippingMethod === m.id ? "border-terra bg-terra-light/60" : "border-linen bg-white hover:border-terra/50"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-espresso">{m.name}</span>
                    <span className="font-mono text-sm">{m.price === 0 ? "Free" : inr(m.price)}</span>
                  </div>
                  <p className="mt-1 text-xs text-clay/60">{m.desc}</p>
                </button>
              ))}
            </div>

            {(rzpEnabled || codEnabled) && (
              <div data-testid="payment-method-section">
                <p className="mt-9 font-mono text-[10px] uppercase tracking-[0.25em] text-terra">Step 3 · Payment method</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {rzpEnabled && (
                    <button
                      data-testid="payment-method-online"
                      onClick={() => setPaymentMethod("online")}
                      className={`rounded-xl border p-4 text-left transition-colors duration-200 ${paymentMethod === "online" ? "border-terra bg-terra-light/60" : "border-linen bg-white hover:border-terra/50"}`}
                    >
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-terra-dark" />
                        <span className="text-sm font-semibold text-espresso">Pay online now</span>
                      </div>
                      <p className="mt-1 text-xs text-clay/60">UPI, cards, netbanking & wallets — secure Razorpay checkout</p>
                    </button>
                  )}
                  {codEnabled && (
                    <button
                      data-testid="payment-method-cod"
                      onClick={() => setPaymentMethod("cod")}
                      className={`rounded-xl border p-4 text-left transition-colors duration-200 ${paymentMethod === "cod" ? "border-terra bg-terra-light/60" : "border-linen bg-white hover:border-terra/50"}`}
                    >
                      <div className="flex items-center gap-2">
                        <Banknote className="h-4 w-4 text-terra-dark" />
                        <span className="text-sm font-semibold text-espresso">Cash on Delivery</span>
                      </div>
                      <p className="mt-1 text-xs text-clay/60">{c("cod_note", "Pay in cash or UPI when your order arrives at your door.")}</p>
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="mt-8 flex items-start gap-3 rounded-xl border border-dashed border-terra/40 bg-terra-light/50 p-4">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-terra-dark" />
              {codSelected ? (
                <p className="text-xs leading-relaxed text-clay/80" data-testid="cod-selected-note">
                  <span className="font-semibold text-terra-dark">Cash on Delivery:</span> {c("cod_note", "pay in cash or UPI when your order arrives at your door.")} Please keep exact change handy if paying by cash.
                </p>
              ) : rzpEnabled ? (
                <p className="text-xs leading-relaxed text-clay/80" data-testid="razorpay-enabled-note">
                  <span className="font-semibold text-terra-dark">Secure online payment:</span> after placing your order,
                  pay instantly via Razorpay — UPI, cards, netbanking and wallets accepted.
                </p>
              ) : (
                <p className="text-xs leading-relaxed text-clay/80">
                  <span className="font-semibold text-terra-dark">Payment:</span> {c("checkout_payment_note", "place your order now — we will reach out on your phone/email with a secure Razorpay payment link before dispatch.")}
                </p>
              )}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-2">
          <div className="rounded-2xl border border-linen bg-white p-6 sm:p-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra">Step {(rzpEnabled || codEnabled) ? "4" : "3"} · Review</p>
            <ul className="mt-5 space-y-4">
              {cart.map((i) => (
                <li key={i.id} className="flex items-center gap-3">
                  <img src={i.image} alt={i.name} className="h-14 w-14 rounded-lg object-cover" />
                  <div className="flex-1">
                    <p className="text-sm font-medium leading-tight text-espresso">{i.name}</p>
                    <p className="font-mono text-xs text-clay/60">× {i.qty}</p>
                  </div>
                  <span className="font-mono text-sm">{inr(i.price * i.qty)}</span>
                </li>
              ))}
              {giftUnlocked && (
                <li className="flex items-center gap-3" data-testid="checkout-freebie-row">
                  <img src={giftProduct.image} alt={giftProduct.name} className="h-14 w-14 rounded-lg object-cover" />
                  <div className="flex-1">
                    <p className="text-sm font-medium leading-tight text-espresso">{giftProduct.name}</p>
                    <p className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-sage"><Gift className="h-3 w-3" /> {c("freebie_label", "Free gift")}</p>
                  </div>
                  <span className="font-mono text-sm text-sage">₹0</span>
                </li>
              )}
            </ul>
            <div className="mt-6 space-y-2 border-t border-linen pt-4 text-sm">
              <div className="flex justify-between text-clay/70"><span>Subtotal</span><span className="font-mono">{inr(subtotal)}</span></div>
              {discount > 0 && (
                <div className="flex justify-between text-sage"><span>Coupon {coupon?.code}</span><span className="font-mono">−{inr(discount)}</span></div>
              )}
              <div className="flex justify-between text-clay/70"><span>Shipping</span><span className="font-mono">{shipping === 0 ? "Free" : inr(shipping)}</span></div>
              <div className="flex justify-between border-t border-linen pt-3 text-base font-semibold text-espresso">
                <span>Total</span><span className="font-mono text-lg">{inr(total)}</span>
              </div>
            </div>
            <button
              data-testid="checkout-place-order-button"
              onClick={placeOrder}
              disabled={placing || cart.length === 0}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-terra py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-terra-dark active:scale-[0.98] disabled:opacity-50"
            >
              {placing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {placing ? "Placing your order…" : codSelected ? "Place order — pay on delivery" : rzpEnabled ? "Place order & pay" : "Place order"}
            </button>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default Checkout;
